import { QueryErrorView } from '@/components/common/QueryErrorView';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Badge } from '@/components/ui/badge';
import { RadioCard } from '@/features/agents/components/RadioCard';

import { useGetModelCollectionsQuery } from '../api/agentsApi';
import type { ModelCollection } from '../types';

export interface ModelStepProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
}

/**
 * Frame step 2 — model collection pick (design §11): `GET /agent/model-collections`
 * (already `isActive`-filtered server-side) as radio cards — name, description,
 * strategy badge, entry count. Required: the frame disables Continue until a row
 * is selected (§11.2). The empty state names the administrator path (D7) — no
 * self-service dead end.
 */
export function ModelStep({ selectedId, onSelect }: ModelStepProps) {
  const { data, isLoading, isError, error, refetch } = useGetModelCollectionsQuery();

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">The set of language models the agent runs on.</p>
      <p className="text-sm font-medium">Model collection</p>

      {isLoading ? (
        <LoadingRows label="Loading model collections" />
      ) : isError ? (
        <QueryErrorView error={error} label="model collections" onRetry={() => void refetch()} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          title="No model collections"
          description="Ask your administrator to create one, then come back."
        />
      ) : (
        <div className="space-y-2">
          {data.map((collection: ModelCollection) => (
            <RadioCard
              key={collection.id}
              groupName="model-collection"
              value={collection.id}
              checked={selectedId === collection.id}
              onSelect={onSelect}
            >
              <span className="block text-sm font-medium">{collection.name}</span>
              {collection.description ? (
                <span className="block text-sm text-muted-foreground">
                  {collection.description}
                </span>
              ) : null}
              <span className="mt-1 flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="rounded-sm font-mono text-xs">
                  {collection.strategy}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {collection.entries.length} models
                </span>
              </span>
            </RadioCard>
          ))}
        </div>
      )}
    </div>
  );
}
